// =====================================================
// CONSTANTES DA APLICAÇÃO
// =====================================================

export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || 'FalaVipPay',
  VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
  DESCRIPTION: import.meta.env.VITE_APP_DESCRIPTION || 'Sistema de Gestão Financeira',
  API_URL: import.meta.env.VITE_API_URL || 'http://localhost:3001/api',
  WEBSOCKET_URL: import.meta.env.VITE_WEBSOCKET_URL || 'ws://localhost:3001',
} as const;

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'falavippay_auth_token',
  REFRESH_TOKEN: 'falavippay_refresh_token',
  USER_DATA: 'falavippay_user_data',
  COMPANY_DATA: 'falavippay_company_data',
  THEME: 'falavippay_theme',
  PREFERENCES: 'falavippay_preferences',
} as const;

export const QUERY_KEYS = {
  // Auth
  ME: ['auth', 'me'],
  COMPANIES: ['companies'],
  
  // Transactions
  PIX_TRANSACTIONS: ['pix', 'transactions'],
  BANK_SLIPS: ['bankSlips'],
  TRANSFERS: ['transfers'],
  
  // Account
  BALANCE: ['account', 'balance'],
  STATEMENT: ['account', 'statement'],
  
  // Dashboard
  DASHBOARD_STATS: ['dashboard', 'stats'],
  FINANCIAL_CHART: ['dashboard', 'chart'],
  
  // Reports
  TRANSACTIONS_REPORT: ['reports', 'transactions'],
  REVENUE_REPORT: ['reports', 'revenue'],
  
  // Notifications
  NOTIFICATIONS: ['notifications'],
  
  // Users
  USERS: ['users'],
} as const;

export const ROUTES = {
  // Public
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  
  // Private
  DASHBOARD: '/dashboard',
  
  // PIX
  PIX: '/pix',
  PIX_CHARGES: '/pix/charges',
  PIX_PAYMENTS: '/pix/payments',
  PIX_KEYS: '/pix/keys',
  
  // Boletos
  BOLETOS: '/boletos',
  BOLETOS_CREATE: '/boletos/create',
  BOLETOS_DETAIL: '/boletos/:id',
  
  // Transferências
  TRANSFERS: '/transfers',
  TRANSFERS_CREATE: '/transfers/create',
  TRANSFERS_DETAIL: '/transfers/:id',
  
  // Conta
  ACCOUNT: '/account',
  ACCOUNT_BALANCE: '/account/balance',
  ACCOUNT_STATEMENT: '/account/statement',
  
  // Relatórios
  REPORTS: '/reports',
  REPORTS_TRANSACTIONS: '/reports/transactions',
  REPORTS_REVENUE: '/reports/revenue',
  
  // Configurações
  SETTINGS: '/settings',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_COMPANY: '/settings/company',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_WEBHOOKS: '/settings/webhooks',
  SETTINGS_SECURITY: '/settings/security',
  
  // Ajuda
  HELP: '/help',
  SUPPORT: '/support',
} as const;

export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MANAGER: 'manager',
  OPERATOR: 'operator',
  VIEWER: 'viewer',
} as const;

export const USER_PERMISSIONS = {
  // PIX
  PIX_CREATE: 'pix.create',
  PIX_READ: 'pix.read',
  PIX_CANCEL: 'pix.cancel',
  
  // Boletos
  BOLETO_CREATE: 'boleto.create',
  BOLETO_READ: 'boleto.read',
  BOLETO_CANCEL: 'boleto.cancel',
  
  // Transferências
  TRANSFER_CREATE: 'transfer.create',
  TRANSFER_READ: 'transfer.read',
  
  // Relatórios
  REPORTS_VIEW: 'reports.view',
  REPORTS_EXPORT: 'reports.export',
  
  // Usuários
  USERS_MANAGE: 'users.manage',
  
  // Empresa
  COMPANY_MANAGE: 'company.manage',
} as const;

export const TRANSACTION_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
} as const;

export const BANK_SLIP_STATUS = {
  PENDING: 'pending',
  REGISTERED: 'registered',
  PAID: 'paid',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  PROTESTED: 'protested',
} as const;

export const TRANSFER_STATUS = {
  PENDING: 'pending',
  SCHEDULED: 'scheduled',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

export const PIX_KEY_TYPES = {
  CPF: 'cpf',
  CNPJ: 'cnpj',
  EMAIL: 'email',
  PHONE: 'phone',
  EVP: 'evp',
} as const;

export const BANKS = [
  { code: '001', name: 'Banco do Brasil' },
  { code: '033', name: 'Santander' },
  { code: '104', name: 'Caixa Econômica Federal' },
  { code: '237', name: 'Bradesco' },
  { code: '341', name: 'Itaú' },
  { code: '756', name: 'Sicoob' },
  { code: '748', name: 'Sicredi' },
  { code: '260', name: 'Nu Pagamentos' },
  { code: '323', name: 'Mercado Pago' },
  { code: '290', name: 'PagSeguro' },
] as const;

export const ACCOUNT_TYPES = [
  { value: 'CC', label: 'Conta Corrente' },
  { value: 'PP', label: 'Poupança' },
  { value: 'PG', label: 'Pagamento' },
] as const;

export const STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
] as const;

export const DATE_FORMATS = {
  DEFAULT: 'DD/MM/YYYY',
  WITH_TIME: 'DD/MM/YYYY HH:mm',
  WITH_SECONDS: 'DD/MM/YYYY HH:mm:ss',
  ISO: 'YYYY-MM-DD',
  US: 'MM/DD/YYYY',
} as const;

export const CURRENCY = {
  SYMBOL: 'R$',
  CODE: 'BRL',
  LOCALE: 'pt-BR',
} as const;

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
} as const;

export const FILE_TYPES = {
  PDF: 'application/pdf',
  EXCEL: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  CSV: 'text/csv',
  IMAGE: 'image/*',
} as const;

export const MAX_FILE_SIZE = {
  IMAGE: 5 * 1024 * 1024, // 5MB
  DOCUMENT: 10 * 1024 * 1024, // 10MB
} as const;

export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  CPF: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
  CNPJ: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
  PHONE: /^\(\d{2}\)\s\d{4,5}-\d{4}$/,
  CEP: /^\d{5}-\d{3}$/,
  PIX_KEY_EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PIX_KEY_PHONE: /^\+55\d{2}\d{8,9}$/,
} as const;

export const ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
} as const;

export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
} as const;

export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} as const;

export const CHART_COLORS = {
  PRIMARY: '#1976d2',
  SECONDARY: '#dc004e',
  SUCCESS: '#2e7d32',
  WARNING: '#ed6c02',
  ERROR: '#d32f2f',
  INFO: '#0288d1',
  GREY: '#757575',
} as const;

export const WEBSOCKET_EVENTS = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  ERROR: 'error',
  PIX_RECEIVED: 'pix:received',
  BOLETO_PAID: 'boleto:paid',
  BALANCE_UPDATED: 'balance:updated',
  NOTIFICATION: 'notification',
} as const;

export const POLLING_INTERVALS = {
  BALANCE: 30000, // 30 seconds
  NOTIFICATIONS: 60000, // 1 minute
  TRANSACTIONS: 120000, // 2 minutes
} as const;